/**
 * AdminToolsInsights — /admin/tools-insights
 * KPIs + 30-day charts + recent trade-ins.
 */
import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Banknote, Car, PieChart, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";

const fmtUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format((cents || 0) / 100);

const KpiCard = ({ label, value, sub, tone = "slate", icon: Icon, testId }) => {
  const tones = {
    slate:   "bg-white text-slate-900 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    gold:    "bg-[#F7EFD1] text-[#7A5C0E] border-[#F1E1A8]",
    brand:   "bg-brand text-white border-brand",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className={`text-[10px] uppercase tracking-widest font-semibold ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{label}</p>
        {Icon && <Icon size={14} className={tone === "brand" ? "text-white/80" : "text-slate-400"} />}
      </div>
      <p className="mt-1.5 font-display text-[24px] font-semibold">{value}</p>
      {sub && <p className={`text-[11px] ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
};

const AdminToolsInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics/tools")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminPage
      title="Tools insights"
      subtitle="Financing calculator + trade-in valuation usage across the Belomotors marketplace."
      testId="admin-tools-insights-page"
    >
      {loading || !data ? (
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      ) : (
        <>
          {/* KPI grids — financing + trade-in blocks */}
          <div className="rounded-2xl border-2 border-emerald-500/40 bg-white p-4 mb-4" data-testid="tools-insights-financing-block">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                <Banknote size={14} />
              </span>
              <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Financing calculator</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <KpiCard label="Total quotes" value={data.financing.total} icon={PieChart} testId="tools-fin-kpi-total" />
              <KpiCard label="Saved" value={data.financing.saved} tone="emerald" testId="tools-fin-kpi-saved" />
              <KpiCard label="Converted to leads" value={data.financing.leads_created} tone="brand" icon={TrendingUp} testId="tools-fin-kpi-leads" />
              <KpiCard
                label="Quote → lead"
                value={`${data.financing.quote_to_lead_conversion_pct}%`}
                sub="Conversion"
                tone="gold"
                testId="tools-fin-kpi-conversion"
              />
              <KpiCard
                label="Avg monthly"
                value={fmtUSD(data.financing.avg_monthly_payment_cents)}
                testId="tools-fin-kpi-avg-monthly"
              />
            </div>
          </div>

          <div className="rounded-2xl border-2 border-gold/60 bg-white p-4 mb-4" data-testid="tools-insights-tradein-block">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-full bg-gold text-slate-900 flex items-center justify-center">
                <Car size={14} />
              </span>
              <p className="text-xs uppercase tracking-widest font-semibold text-[#7A5C0E]">Trade-in valuation</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <KpiCard label="Total estimates" value={data.trade_in.total} icon={PieChart} testId="tools-ti-kpi-total" />
              <KpiCard label="Submitted" value={data.trade_in.submitted} tone="gold" testId="tools-ti-kpi-submitted" />
              <KpiCard label="Converted to leads" value={data.trade_in.leads_created} tone="brand" icon={TrendingUp} testId="tools-ti-kpi-leads" />
              <KpiCard
                label="Estimate → lead"
                value={`${data.trade_in.estimate_to_lead_conversion_pct}%`}
                sub="Conversion"
                tone="emerald"
                testId="tools-ti-kpi-conversion"
              />
              <KpiCard
                label="Avg estimate"
                value={fmtUSD(data.trade_in.avg_estimate_mid_cents)}
                sub="Mid value"
                testId="tools-ti-kpi-avg-estimate"
              />
            </div>
          </div>

          {/* Combined 30d chart */}
          <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4" data-testid="tools-insights-timeseries">
            <div className="mb-3">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Daily usage · last 30 days</p>
              <p className="font-display text-lg font-semibold text-slate-900">Financing quotes · Trade-in estimates</p>
            </div>
            <div className="h-72">
              {data.time_series?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.time_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="finance" fill="#059669" name="Financing quotes" />
                    <Bar dataKey="tradein" fill="#D4A537" name="Trade-in estimates" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">
                  No activity yet.
                </div>
              )}
            </div>
          </div>

          {/* Recent trade-ins table */}
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="tools-insights-recent-tradeins">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent trade-in estimates</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Vehicle</th>
                  <th className="text-right px-4 py-2 font-semibold">Mileage</th>
                  <th className="text-left px-4 py-2 font-semibold">Condition</th>
                  <th className="text-right px-4 py-2 font-semibold">Estimate range</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_trade_ins || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-sm text-slate-400 px-4 py-6">
                      No trade-in estimates yet.
                    </td>
                  </tr>
                ) : data.recent_trade_ins.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`tools-insights-tradein-row-${e.id}`}>
                    <td className="px-4 py-2">
                      <span className="font-medium">{e.year} {e.make} {e.model}</span>
                      {e.trim && <span className="text-slate-500 text-xs"> ({e.trim})</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-slate-700">{Number(e.mileage).toLocaleString()}</td>
                    <td className="px-4 py-2 capitalize">{e.condition}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-800">
                      {fmtUSD(e.estimate_low_cents)}–{fmtUSD(e.estimate_high_cents)}
                    </td>
                    <td className="px-4 py-2">
                      {e.submitted_to_dealer ? (
                        <span className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 font-semibold">Submitted</span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Draft</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {e.created_at ? new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminPage>
  );
};

export default AdminToolsInsights;
